import { fetchReturnValuations } from 'services/returnValuationsApi';
import {
  fetchPortfolioBalances,
  PortfolioBalancesResponse,
} from 'views/Portfolio/components/Summary/services/fetchPortfolioBalances';
import { PortfolioBalance } from 'types/DomainTypes';
import { PERIODS, calculatePeriodReturn } from 'helpers/portfolioReturns';
import { formatCurrency, formatPercentage } from '../../../AIChat/utils/formatters';

export const buildValuationContext = async (): Promise<string> => {
  // Get account context to find accountHolderId
  const balancesResponse: PortfolioBalancesResponse = await fetchPortfolioBalances();
  const rawData: PortfolioBalance[] = balancesResponse?.data || [];

  if (rawData.length === 0) {
    return 'No valuation history available.';
  }

  const accountHolderId = rawData.find((p) => p.accountHolderId)?.accountHolderId;
  if (!accountHolderId) {
    return 'No valuation history available.';
  }

  const response = await fetchReturnValuations({
    acctHolderId: accountHolderId,
    pageSize: 100,
  });

  const valuations = response?.data || [];
  if (valuations.length === 0) {
    return 'No valuation history available.';
  }

  const lines: string[] = [];

  // Sort by date
  const sorted = [...valuations].sort(
    (a, b) => new Date(a.valuationDate).getTime() - new Date(b.valuationDate).getTime(),
  );

  const earliest = sorted[0];
  const latest = sorted[sorted.length - 1];

  // Calculate tenure
  const startDate = new Date(earliest.valuationDate);
  const endDate = new Date(latest.valuationDate);
  const tenureMonths = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30));

  // Find peak NAV
  let peakNav = 0;
  let peakNavDate = '';
  sorted.forEach((v) => {
    const nav = v.valuationEnd || 0;
    if (nav > peakNav) {
      peakNav = nav;
      peakNavDate = v.valuationDate;
    }
  });

  const currentNav = latest.valuationEnd || 0;
  const currentNetPosition = latest.netPosition || 0;
  const currentNetPositionPercent = latest.totalNetPositionPercent || 0;
  const drawdown = peakNav > 0 ? ((peakNav - currentNav) / peakNav) * 100 : 0;

  // Total flows
  const totalPaidIn = sorted.reduce((sum, v) => sum + (v.inFlow || 0), 0);
  const totalWithdrawn = sorted.reduce((sum, v) => sum + (v.outFlow || 0), 0);

  lines.push('## Account Valuation History');
  lines.push('');
  lines.push('### Account Overview');
  lines.push(`- Account Tenure: ${tenureMonths} months (${(tenureMonths / 12).toFixed(1)} years)`);
  lines.push('');

  lines.push('### Current Position');
  lines.push(`- Net Asset Value: ${formatCurrency(currentNav)}`);
  lines.push(
    `- Net Position (Profit): ${formatCurrency(currentNetPosition)} (${formatPercentage(currentNetPositionPercent)})`,
  );
  lines.push('');

  lines.push('### Capital Journey');
  lines.push(`- Total Capital Invested: ${formatCurrency(totalPaidIn)}`);
  lines.push(`- Total Withdrawn: ${formatCurrency(totalWithdrawn)}`);
  lines.push('');

  lines.push('### Performance Milestones');
  lines.push(`- All-Time High NAV: ${formatCurrency(peakNav)} (${peakNavDate})`);
  lines.push(`- Current Drawdown from Peak: -${drawdown.toFixed(1)}%`);
  lines.push('');

  // Period returns on the same basis as the Portfolio Performance chart. The response covers
  // every portfolio on the account, so collapse to one point per date before measuring - and
  // where a date spans several portfolios, drop the per-portfolio monthly return and let the
  // maths fall back to Modified Dietz over the combined NAV.
  const byDate = new Map<
    string,
    { currentHoldings: number; netContributions: number; oneMonthReturn: number | null }
  >();
  sorted.forEach((v) => {
    const existing = byDate.get(v.valuationDate);
    if (existing) {
      existing.currentHoldings += v.nav ?? 0;
      existing.netContributions += v.cumNetCapitalInvested ?? 0;
      existing.oneMonthReturn = null;
    } else {
      byDate.set(v.valuationDate, {
        currentHoldings: v.nav ?? 0,
        netContributions: v.cumNetCapitalInvested ?? 0,
        oneMonthReturn: v.oneMonthHoldingReturn,
      });
    }
  });

  const combinedSeries = Array.from(byDate.entries())
    .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
    .map(([, point]) => point);

  const periodLines = PERIODS.map(({ label, months }) => {
    const { percent, monthsUsed, isPartialPeriod } = calculatePeriodReturn(combinedSeries, months);
    if (percent === null) return `- ${label}: not enough history`;
    const caveat = isPartialPeriod ? ` (only ${monthsUsed} months of history available)` : '';
    return `- ${label}: ${formatPercentage(percent)}${caveat}`;
  });

  lines.push('### Portfolio Returns by Period');
  lines.push('Return after money paid in and taken out. This is the authoritative measure of how');
  lines.push('this portfolio has performed - use these figures, not average wine price movements.');
  periodLines.forEach((line) => lines.push(line));

  return lines.join('\n');
};
